(() => {
  class NewsletterPopup extends ModalDialog {
    constructor() {
      super();
      const delaySeconds = Number.parseInt(this.dataset.delay, 10);
      const reopenDays = Number.parseInt(this.dataset.reopen, 10);

      this.delay = Number.isFinite(delaySeconds) ? delaySeconds * 1000 : 2000;
      this.reopenDays = Number.isFinite(reopenDays) ? reopenDays : 1;
      this.sectionId = this.dataset.sectionId;

      this.handleOpenerClose = this.handleOpenerClose.bind(this);
      this.handleSectionSelect = this.handleSectionSelect.bind(this);
      this.handleSectionDeselect = this.handleSectionDeselect.bind(this);
    }

    connectedCallback() {
      super.connectedCallback();

      this.modalOpener = this.parentElement?.querySelector(`modal-opener[data-modal="#${this.id}"]`);
      this.closeButton = this.modalOpener?.querySelector('.newsletter-modal__opener-close');
      this.closeButton?.addEventListener('click', this.handleOpenerClose);
      this.showPopupAfterDelay();

      if (Shopify.designMode) {
        document.addEventListener('shopify:section:select', this.handleSectionSelect);
        document.addEventListener('shopify:section:deselect', this.handleSectionDeselect);
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      clearTimeout(this._popupTimer);
      clearTimeout(this._resetTimer);
      this.closeButton?.removeEventListener('click', this.handleOpenerClose);
      document.removeEventListener('shopify:section:select', this.handleSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.handleSectionDeselect);
      this.removeScrollRestoreListener();
    }

    handleSectionSelect(event) {
      if (event.detail.sectionId === this.sectionId) this.show();
    }

    handleSectionDeselect(event) {
      if (event.detail.sectionId === this.sectionId) this.hide();
    }

    showPopupAfterDelay() {
      const modalClosed = localStorage.getItem('newsletterPopupClosed');
      // A stale dismissal would keep the launcher hidden in the theme editor and
      // make the "Enable trigger button" setting look dead, so only honour it for
      // shoppers. The popup's own dismissal still applies, which is what lets an
      // author close the popup and see the launcher underneath.
      const openerClosed = !Shopify.designMode && localStorage.getItem('newsletterOpenerClosed');
      const currentTime = Date.now();
      const modalClosedAt = Number.parseInt(modalClosed, 10);
      const openerClosedAt = Number.parseInt(openerClosed, 10);

      if (!Number.isFinite(modalClosedAt) || currentTime > modalClosedAt) {
        clearTimeout(this._popupTimer);
        this._popupTimer = setTimeout(() => {
          this._popupTimer = null;
          if (!this.isConnected) return;
          this.show();
        }, this.delay);
      }
      if (Number.isFinite(openerClosedAt) && currentTime <= openerClosedAt) {
        this.hideModalOpener();
      }
    }

    /* ModalDialog locks the page with `body { overflow: hidden }`, which stops a
     wheel or touch gesture but not a programmatic scroll — focus(),
     scrollIntoView() and window.scrollTo() all still move the viewport, and on
     iOS the class does not hold at all. This section renders after the footer,
     so anything that scrolls while the popup is up strands the shopper at the
     bottom of the page once it closes. Remember where they were. */
    show(opener) {
      clearTimeout(this._popupTimer);
      this._popupTimer = null;
      clearTimeout(this._resetTimer);
      this.lockedScrollY = window.scrollY;
      super.show(opener);
    }

    hide() {
      if (this.hasAttribute('closing')) return;

      clearTimeout(this._popupTimer);
      this._popupTimer = null;
      const popupForm = this.querySelector('newsletter-popup-form');
      popupForm?.cancelAutoClose();
      this.restoreScrollOnClose();
      super.hide();

      if (!Shopify.designMode) {
        const expirationDate = Date.now() + this.reopenDays * 24 * 60 * 60 * 1000;
        localStorage.setItem('newsletterPopupClosed', expirationDate);
      }

      this._resetTimer = setTimeout(() => {
        this._resetTimer = null;
        popupForm?.reset();
      }, 550);
    }

    /* ModalDialog dispatches `modalClosed` from the same callback that unlocks
     the body, so restoring there lands in the frame the page becomes scrollable
     again — no visible jump, and no second guess at its close timing. */
    restoreScrollOnClose() {
      const { lockedScrollY } = this;
      if (typeof lockedScrollY !== 'number') return;

      this.removeScrollRestoreListener();
      this._handleModalClosed = () => {
        if (window.scrollY !== lockedScrollY) window.scrollTo(0, lockedScrollY);
        this._handleModalClosed = null;
      };
      document.body.addEventListener('modalClosed', this._handleModalClosed, { once: true });
    }

    removeScrollRestoreListener() {
      if (!this._handleModalClosed) return;
      document.body.removeEventListener('modalClosed', this._handleModalClosed);
      this._handleModalClosed = null;
    }

    handleOpenerClose() {
      this.hideModalOpener();
      if (Shopify.designMode) return;

      const openerExpirationDate = Date.now() + this.reopenDays * 24 * 60 * 60 * 1000;
      localStorage.setItem('newsletterOpenerClosed', openerExpirationDate);
    }

    hideModalOpener() {
      if (this.modalOpener) {
        this.modalOpener.style.display = 'none';
      }
    }
  }

  if (!customElements.get('newsletter-popup')) {
    customElements.define('newsletter-popup', NewsletterPopup);
  }

  /**
   * <newsletter-popup-form> — signs the shopper up through the Atomic middleware
   * (ArmadaApi), the same backend the newsletter page uses, and swaps the panel
   * over to the confirmation view on success.
   *
   * The popup only asks for an email, so the CRM's personal fields go out empty:
   * { email, firstName: '', lastName: '', country, interests, newsletters }.
   * `gender` and `birthdate` are omitted entirely.
   *
   * Markup contract:
   *   [data-newsletters]                 — JSON array of CRM newsletter codes
   *   [data-crm-origin]                  — CRM signup origin
   *   [data-country]                     — active market's ISO country code
   *   form[data-newsletter-popup]        — the form
   *   [name="email"]                     — email (required)
   *   [name="accepts_marketing"]         — consent checkbox (required)
   *   [name="interests"]                 — interest chips (0+), outside the form;
   *                                        an empty selection submits all of them
   *   [data-interest-all]                — "All" chip; a UI shortcut, not a CRM code
   *   [data-newsletter-popup-status]     — status message target
   *   [data-popup-view="form"|"confirmation"]
   */
  class NewsletterPopupForm extends HTMLElement {
    constructor() {
      super();
      this.endpoint = this.dataset.endpoint || '/customers/newsletter';
      this.strings = window.newsletterPopupStrings || {};
      this.newsletterCodes = NewsletterPopupForm.parseCodes(this.dataset.newsletters);
      this.autoCloseDelay = 2000;
      // The CRM-agreed origin lives in the theme editor alone, so there is no
      // fallback here — clearing the setting deliberately sends an empty origin.
      this.crmOrigin = (this.dataset.crmOrigin || '').trim();
      // There is no country field in the popup, so the CRM gets the storefront's
      // active market (rendered by Liquid as data-country).
      this.country = (this.dataset.country || '').trim();

      this.form = this.querySelector('form[data-newsletter-popup]');
      this.formView = this.querySelector('[data-popup-view="form"]');
      this.confirmationView = this.querySelector('[data-popup-view="confirmation"]');
      this.allChip = this.querySelector('[data-interest-all]');
      this.interestInputs = Array.from(this.querySelectorAll('[name="interests"]'));

      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleAllChipChange = this.handleAllChipChange.bind(this);
      this.handleInterestChange = this.handleInterestChange.bind(this);
    }

    static parseCodes(raw) {
      if (!raw) return [];
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.map((code) => String(code).trim()).filter(Boolean);
      } catch (_err) {
        /* ignore malformed value */
      }
      return [];
    }

    connectedCallback() {
      this.form?.addEventListener('submit', this.handleSubmit);
      this.allChip?.addEventListener('change', this.handleAllChipChange);
      this.interestInputs.forEach((input) => input.addEventListener('change', this.handleInterestChange));
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.allChip?.removeEventListener('change', this.handleAllChipChange);
      this.interestInputs.forEach((input) => input.removeEventListener('change', this.handleInterestChange));
      this.cancelAutoClose();
    }

    // "All" is a UI shortcut: ticking it ticks every interest, clearing it clears them.
    handleAllChipChange() {
      const { checked } = this.allChip;
      this.interestInputs.forEach((input) => {
        input.checked = checked;
      });
    }

    // Keep "All" in sync — it stays ticked only while every interest is ticked.
    handleInterestChange() {
      if (!this.allChip) return;
      this.allChip.checked = this.interestInputs.length > 0 && this.interestInputs.every((input) => input.checked);
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (!this.form || this.form.dataset.loading === 'true') return;

      const data = new FormData(this.form);
      this.setStatus('', null);

      if (!this.validate(data)) return;

      if (!window.ArmadaApi) {
        this.setStatus(this.strings.error, 'error');
        return;
      }

      // Read the chips off the element, not the FormData: they sit above the
      // <form> in the layout, so they are not among its controls. Nothing picked
      // means "everything" — an empty selection subscribes to all of them.
      const picked = this.interestInputs.filter((input) => input.checked).map((input) => input.value);
      const interests = picked.length > 0 ? picked : this.interestInputs.map((input) => input.value);

      const payload = {
        email: (data.get('email') || '').toString().trim(),
        firstName: '',
        lastName: '',
        country: this.country,
        interests,
        newsletters: this.newsletterCodes.map((code) => ({
          code,
          subscribed: true,
          origin: this.crmOrigin,
        })),
      };

      this.setLoading(true);

      // The CRM endpoint rejects unrecognized keys, so opt out of the client's
      // automatic `locale` injection.
      const res = await window.ArmadaApi.post(this.endpoint, payload, { includeLocale: false });

      if (!this.isConnected) return;
      this.setLoading(false);

      if (res.ok) {
        this.showConfirmation();
      } else {
        this.setStatus((res.error && res.error.message) || this.strings.error, 'error');
      }
    }

    validate(data) {
      const email = (data.get('email') || '').toString().trim();
      if (!email) return this.failEmail(this.strings.emailRequired);
      if (!NewsletterPopupForm.isValidEmail(email)) return this.failEmail(this.strings.emailInvalid);
      if (!data.get('accepts_marketing')) return this.failConsent(this.strings.consentRequired);
      return true;
    }

    static isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }

    failEmail(message) {
      this.setStatus(message || this.strings.error, 'error');
      const input = this.form.querySelector('[name="email"]');
      if (input) {
        input.setAttribute('aria-invalid', 'true');
        input.addEventListener('input', () => input.removeAttribute('aria-invalid'), { once: true });
        input.focus({ preventScroll: true });
      }
      return false;
    }

    failConsent(message) {
      this.setStatus(message || this.strings.error, 'error');
      const input = this.form.querySelector('[name="accepts_marketing"]');
      if (input) {
        const row = input.closest('.newsletter-modal__consent');
        row?.classList.add('newsletter-modal__consent--invalid');
        input.addEventListener('change', () => row?.classList.remove('newsletter-modal__consent--invalid'), {
          once: true,
        });
        input.focus({ preventScroll: true });
      }
      return false;
    }

    setLoading(isLoading) {
      if (!this.form) return;
      this.form.dataset.loading = isLoading ? 'true' : 'false';
      const button = this.form.querySelector('button[type="submit"]');
      if (!button) return;

      button.disabled = isLoading;
      if (isLoading) {
        button.dataset.label = button.dataset.label || button.textContent;
        if (this.strings.loading) button.textContent = this.strings.loading;
      } else if (button.dataset.label) {
        button.textContent = button.dataset.label;
      }
    }

    setStatus(message, type) {
      const status = this.form.querySelector('[data-newsletter-popup-status]');
      if (!status) return;

      status.textContent = message || '';
      status.hidden = !message;
      status.classList.toggle('newsletter-modal__status--error', type === 'error');

      if (message && type === 'error') status.setAttribute('role', 'alert');
      else status.setAttribute('role', 'status');
    }

    showConfirmation() {
      if (!this.confirmationView) return;

      if (this.formView) this.formView.hidden = true;
      this.setFormControlsDisabled(true);
      this.confirmationView.hidden = false;

      const heading = this.confirmationView.querySelector('.newsletter-modal__heading');
      const dialog = this.closest('newsletter-popup')?.querySelector('[role="dialog"]');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        if (dialog && heading.id) dialog.setAttribute('aria-labelledby', heading.id);

        if (dialog && typeof trapFocus === 'function') {
          trapFocus(dialog, null);
        }
        heading.focus({ preventScroll: true });
      }

      this.scheduleAutoClose();
    }

    scheduleAutoClose() {
      this.cancelAutoClose();
      const modal = this.closest('newsletter-popup');
      if (!modal || typeof modal.hide !== 'function') return;

      this._closeTimer = setTimeout(() => {
        this._closeTimer = null;
        modal.hide();
      }, this.autoCloseDelay);
    }

    cancelAutoClose() {
      clearTimeout(this._closeTimer);
      this._closeTimer = null;
    }

    setFormControlsDisabled(isDisabled) {
      this.formView?.querySelectorAll('button, input, select, textarea').forEach((control) => {
        control.disabled = isDisabled;
      });
    }

    reset() {
      this.cancelAutoClose();
      this.setFormControlsDisabled(false);
      this.form?.reset();
      this.setLoading(false);
      this.setStatus('', null);
      this.form?.querySelector('[name="email"]')?.removeAttribute('aria-invalid');
      this.form?.querySelector('.newsletter-modal__consent')?.classList.remove('newsletter-modal__consent--invalid');

      if (this.allChip) this.allChip.checked = false;
      this.interestInputs.forEach((input) => {
        input.checked = false;
      });

      if (this.formView) this.formView.hidden = false;
      if (this.confirmationView) this.confirmationView.hidden = true;

      const formHeading = this.formView?.querySelector('.newsletter-modal__heading');
      const dialog = this.closest('newsletter-popup')?.querySelector('[role="dialog"]');
      if (dialog && formHeading?.id) dialog.setAttribute('aria-labelledby', formHeading.id);
    }
  }

  if (!customElements.get('newsletter-popup-form')) {
    customElements.define('newsletter-popup-form', NewsletterPopupForm);
  }
})();
